import { forwardRef, useMemo, useState } from 'react'
import {
  Card, Checkbox, Chip, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TableSortLabel, Typography,
} from '@mui/material'
import { TableVirtuoso, type TableComponents } from 'react-virtuoso'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import { AssigneeAvatars, PriorityChip, StatusDot } from '../components/chips'
import { TYPE_LABEL, type DummyWorkOrder } from '../dummy'
import { useSelectWorkOrder, useWorkOrderBulk, useWorkOrderRowMenu } from '../WorkOrdersPage'
import { useOrgSettings } from '../../../hooks/useOrgSettings'

type SortKey = 'id' | 'priority' | 'dueDate' | 'status' | 'type'
type SortDir = 'asc' | 'desc'

const PRIORITY_ORDER: Record<string, number> = {
  critical: 4, high: 3, medium: 2, low: 1,
}
const STATUS_ORDER: Record<string, number> = {
  scheduled: 0, open: 1, in_progress: 2, closed: 3, resolved: 4,
}

const dayMs = 24 * 60 * 60 * 1000

export function TableView({ workOrders }: { workOrders: DummyWorkOrder[] }) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'priority', dir: 'desc' })
  const select = useSelectWorkOrder()
  const openRowMenu = useWorkOrderRowMenu()
  const bulk = useWorkOrderBulk()
  const { formatDateShort } = useOrgSettings()

  // Memoised — without this every keystroke / chip click in the
  // toolbar re-sorts thousands of rows synchronously.
  const sorted = useMemo(() => {
    const copy = [...workOrders]
    copy.sort((a, b) => {
      let cmp = 0
      switch (sort.key) {
        case 'id':       cmp = a.id.localeCompare(b.id); break
        case 'priority': cmp = PRIORITY_ORDER[a.priority]! - PRIORITY_ORDER[b.priority]!; break
        case 'status':   cmp = STATUS_ORDER[a.status]! - STATUS_ORDER[b.status]!; break
        case 'dueDate':  cmp = a.dueDate.getTime() - b.dueDate.getTime(); break
        case 'type':     cmp = a.type.localeCompare(b.type); break
      }
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [workOrders, sort.key, sort.dir])

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'desc' }
    )
  }

  // All row ids upfront for the header checkbox's all/some-selected
  // checks. Pre-extract so each render isn't doing 3K .map() calls.
  const allRowIds = useMemo(() => workOrders.map((w) => w._raw.id), [workOrders])

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* TableVirtuoso preserves semantic <table> (so headers stay
          sticky, sort labels still work, screen readers see a table)
          while only mounting the rows currently in view. Cuts a
          3K-row mount from ~3s to ~150ms. */}
      <TableVirtuoso
        style={{ height: '100%' }}
        data={sorted}
        components={VIRTUOSO_COMPONENTS}
        // Context flows into the TableRow component override so each
        // virtualised row can fire `select(wo)` on click without us
        // having to clone the component map per parent.
        context={{ select, openRowMenu }}
        fixedHeaderContent={() => (
          <TableRow>
            {bulk && (
              <TableCell padding="checkbox">
                <Checkbox size="small"
                  checked={bulk.allSelected(allRowIds)}
                  indeterminate={bulk.someSelected(allRowIds)}
                  onChange={(e) => bulk.replaceWith(
                    e.target.checked ? allRowIds : [])} />
              </TableCell>
            )}
            <SortHeader onClick={() => toggleSort('status')}   active={sort.key === 'status'}   dir={sort.dir}>Status</SortHeader>
            <SortHeader onClick={() => toggleSort('id')}       active={sort.key === 'id'}       dir={sort.dir}>ID</SortHeader>
            <TableCell>Title</TableCell>
            <SortHeader onClick={() => toggleSort('priority')} active={sort.key === 'priority'} dir={sort.dir}>Priority</SortHeader>
            <TableCell>Asset / Location</TableCell>
            <TableCell>Assigned</TableCell>
            <SortHeader onClick={() => toggleSort('dueDate')}  active={sort.key === 'dueDate'}  dir={sort.dir}>Due</SortHeader>
            <TableCell>SLA</TableCell>
            <SortHeader onClick={() => toggleSort('type')}     active={sort.key === 'type'}     dir={sort.dir}>Type</SortHeader>
          </TableRow>
        )}
        itemContent={(_idx, wo) => {
          const daysUntil = Math.round((wo.dueDate.getTime() - Date.now()) / dayMs)
          const overdue = daysUntil < 0 && wo.status !== 'closed' && wo.status !== 'resolved'
          const dueLabel =
            daysUntil === 0 ? 'Today' :
            daysUntil === 1 ? 'Tomorrow' :
            daysUntil === -1 ? 'Yesterday' :
            daysUntil < 0 ? `${-daysUntil}d overdue` :
            formatDateShort(wo.dueDate)

          return (
            <>
              {bulk && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={bulk.isSelected(wo._raw.id)}
                    onClick={(e) => { e.stopPropagation(); bulk.toggle(wo._raw.id) }}
                    onChange={() => {}} />
                </TableCell>
              )}
              <TableCell><StatusDot status={wo.status} /></TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>
                  {wo._raw.wo_code || (wo.id.length > 12 ? `${wo._raw.kind === 'task' ? 'TSK' : 'WO'}-${wo.id.slice(0, 6).toUpperCase()}` : wo.id)}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 360 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                    {wo.title}
                  </Typography>
                  {wo._raw.pending_my_approval && (
                    <Chip
                      label="PENDING APPROVAL"
                      size="small"
                      sx={{
                        height: 18, fontSize: 9, fontWeight: 700, flexShrink: 0,
                        letterSpacing: '0.04em',
                        bgcolor: '#FFFBEB', color: '#B45309',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                  {wo.description}
                </Typography>
              </TableCell>
              <TableCell><PriorityChip priority={wo.priority} /></TableCell>
              <TableCell sx={{ maxWidth: 220 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>
                  {wo.asset?.name ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
                  {wo.location}
                </Typography>
              </TableCell>
              <TableCell>
                <AssigneeAvatars assignees={wo.assignees} max={3} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <ScheduleOutlinedIcon
                    sx={{ fontSize: 14, color: overdue ? '#DC2626' : 'text.secondary' }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: overdue ? 700 : 500,
                      color: overdue ? '#DC2626' : 'text.primary',
                    }}
                  >
                    {dueLabel}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <SlaCell wo={wo} />
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {TYPE_LABEL[wo.type]}
                </Typography>
              </TableCell>
            </>
          )
        }}
      />
    </Card>
  )
}

// TableVirtuoso component overrides — wire its scroll container, head,
// body, and row to the MUI primitives so theming + sort labels + the
// sticky header all keep working. Defined outside the component so
// the same identity is reused across renders (Virtuoso treats new
// component refs as a full remount).
const VIRTUOSO_COMPONENTS: TableComponents<DummyWorkOrder, {
  select?: (wo: DummyWorkOrder) => void
  openRowMenu?: (e: React.MouseEvent, wo: DummyWorkOrder) => void
}> = {
  Scroller: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function Scroller(props, ref) {
      return <div {...props} ref={ref} style={{ ...props.style, overflow: 'auto' }} />
    },
  ),
  Table: (props) => (
    <Table
      {...props}
      stickyHeader
      sx={{
        borderCollapse: 'separate',
        '& thead th': {
          bgcolor: 'background.paper',
          fontSize: 11.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.25,
        },
      }}
    />
  ),
  TableHead: forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    function VHead(props, ref) {
      return <TableHead {...props} ref={ref} />
    },
  ),
  TableRow: ({ item, context, ...props }) => (
    <TableRow
      {...props}
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() => context?.select?.(item)}
      onContextMenu={(e) => context?.openRowMenu?.(e, item)}
    />
  ),
  TableBody: forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    function VBody(props, ref) {
      return <TableBody {...props} ref={ref} />
    },
  ),
}

function SortHeader({
  onClick, active, dir, children,
}: {
  onClick: () => void
  active: boolean
  dir: SortDir
  children: React.ReactNode
}) {
  return (
    <TableCell>
      <TableSortLabel
        active={active}
        direction={active ? dir : 'asc'}
        onClick={onClick}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  )
}

function SlaCell({ wo }: { wo: DummyWorkOrder }) {
  const raw = wo._raw
  if (!raw.sla_policy_id) return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>
  if (raw.status === 'resolved') return <Typography sx={{ fontSize: 11.5, color: '#059669', fontWeight: 600 }}>Met</Typography>

  const now = Date.now()
  const responseDue = raw.sla_response_due ? new Date(raw.sla_response_due).getTime() : null
  const resolutionDue = raw.sla_resolution_due ? new Date(raw.sla_resolution_due).getTime() : null
  const respondedAt = raw.sla_responded_at ? new Date(raw.sla_responded_at).getTime() : null

  let label = ''
  let breached = !!raw.sla_breached

  if (!respondedAt && responseDue) {
    const diff = responseDue - now
    label = diff <= 0 ? `−${fmtShort(Math.abs(diff))}` : fmtShort(diff)
    if (diff <= 0) breached = true
  } else if (resolutionDue && raw.status !== 'closed') {
    const diff = resolutionDue - now
    label = diff <= 0 ? `−${fmtShort(Math.abs(diff))}` : fmtShort(diff)
    if (diff <= 0) breached = true
  } else if (raw.status === 'closed') {
    label = 'Met'
  }

  if (!label) return null

  const color = breached ? '#DC2626' : label === 'Met' ? '#059669' : '#D97706'

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <TimerOutlinedIcon sx={{ fontSize: 13, color }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color }}>{label}</Typography>
    </Stack>
  )
}

function fmtShort(ms: number): string {
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}
